import cn from 'classnames'
import { FC } from 'react'
import { createPortal } from 'react-dom'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { ILoaderProps, Loader } from 'core-components/library/loader/Loader'

import styles from './LoaderOverlay.module.scss'

interface Props {
  /**
   * Set the visibility of component
   */
  isVisible: boolean
  /**
   * Render overlay fixed to the viewport, ignoring parent positioning
   * @default false
   */
  isFullScreen?: boolean
  /**
   * Size of the Loader
   */
  dimension?: ILoaderProps['dimension']
  /**
   * Render overlay via a portal (defaults to document.body)
   * @default false
   */
  usePortal?: boolean
  /**
   * Custom portal container element
   * @default document.body
   */
  portalContainer?: Element | DocumentFragment | null
  /**
   * Classes to apply to the overlay
   */
  classes?: {
    overlay?: string
    container?: string
    inner?: string
    loader?: string
    message?: string
  }
  /**
   * Message to display
   */
  message?: React.ReactNode
  /**
   * Background color of the overlay
   * @default 'transparent'
   */
  color?: 'white' | 'black' | 'transparent'
}

export const LoaderOverlay: FC<Props> = ({
  isVisible,
  dimension,
  message,
  classes,
  color = 'black',
  isFullScreen = false,
  usePortal = false,
  portalContainer,
}) => {
  const hasMessage = Boolean(message)

  if (!isVisible) return null

  const overlay = (
    <div
      className={cn(
        styles.overlay,
        styles[color],
        { [styles.fullScreen]: isFullScreen },
        classes?.overlay
      )}
    >
      <div className={cn(styles.container, classes?.container)}>
        <div className={cn({ [styles.inner]: hasMessage }, classes?.inner)}>
          <Loader className={styles.loader} dimension={dimension} />
          <RenderWhen condition={hasMessage}>
            <div className={cn(styles.message, classes?.message)}>
              {typeof message === 'string' ? <Translation text={message} span /> : message}
            </div>
          </RenderWhen>
        </div>
      </div>
    </div>
  )

  if (!usePortal) return overlay

  const portalTarget = portalContainer ?? (typeof document !== 'undefined' ? document.body : null)

  if (!portalTarget) return overlay

  return createPortal(overlay, portalTarget)
}
